import React, { useState, useEffect } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Building2, ArrowRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

export default function CompanyPicker() {
  const { user, refreshUser } = useAuth() || {};
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const list = await apiGet('/companies');
        setCompanies(list);
        // If only 1 → auto-pick and redirect to dashboard
        if (list.length === 1) {
          await pick(list[0].id);
        }
      } catch (err) {
        toast.error('Failed to load companies');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pick = async (cid) => {
    setSwitching(cid);
    try {
      const r = await apiPost(`/companies/switch/${cid}`, {});
      if (r.access_token) localStorage.setItem('access_token', r.access_token);
      localStorage.setItem('active_company_id', cid);
      localStorage.setItem('active_company_name', r.active_company?.name || '');
      if (refreshUser) await refreshUser();
      toast.success(`Switched to ${r.active_company.name}`);
      navigate('/app');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Switch failed');
      setSwitching(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-[#1F7A6E]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50 flex items-center justify-center p-6" data-testid="company-picker">
      <div className="max-w-2xl w-full">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#1F7A6E] font-display font-bold mb-3">
            Step 2 of 2
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900 mb-2">
            Pick your company.
          </h1>
          <p className="text-slate-600 text-sm">
            Hi {user?.name || user?.email?.split('@')[0]} — choose which entity you want to work in today.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="company-grid">
          {companies.map(c => (
            <button
              key={c.id}
              onClick={() => pick(c.id)}
              disabled={switching}
              data-testid={`company-tile-${c.short_key || c.id}`}
              className="group relative bg-white border-2 border-slate-200 hover:border-[#1F7A6E] rounded-sm p-7 text-left transition-all hover:shadow-xl disabled:opacity-50"
              style={{ borderTopColor: c.primary_color || '#1F7A6E', borderTopWidth: 4 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-12 h-12 rounded-sm flex items-center justify-center"
                  style={{ background: c.primary_color || '#1F7A6E', color: 'white' }}
                >
                  <Building2 size={22} />
                </div>
                {c.is_parent && (
                  <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider font-display font-bold bg-teal-100 text-teal-800 rounded-sm">
                    Parent
                  </span>
                )}
              </div>
              <div className="kpi-overline mb-1">{c.entity_type}</div>
              <h3 className="font-display text-lg font-bold text-slate-900 mb-1">{c.name}</h3>
              <p className="text-xs text-slate-500 mb-5">{c.legal_name || c.name}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-[#1F7A6E] font-display font-bold uppercase tracking-wider group-hover:underline">
                  {switching === c.id ? 'Loading…' : 'Enter'}
                </span>
                {switching === c.id ? <Loader2 size={14} className="animate-spin text-[#1F7A6E]" /> : <ArrowRight size={14} className="text-[#1F7A6E] group-hover:translate-x-1 transition-transform" />}
              </div>
            </button>
          ))}
        </div>

        {companies.length === 0 && (
          <div className="text-center text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-6 text-sm">
            You have no companies assigned. Please contact your Super Admin.
          </div>
        )}

        <p className="text-center text-[10px] text-slate-400 mt-8 uppercase tracking-widest">
          You can switch companies anytime from the top-right header.
        </p>
      </div>
    </div>
  );
}
